import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { ReactComponent as CloseSVG } from "images/global/buttonArrow.svg";
import loader from "library/Loader";
import UniversalLink from "library/Loader/UniversalLink";
import { loadPage } from "library/Loader/TransitionUtils";
import libraryConfig from "libraryConfig";
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive";
import getMedia from "library/getMedia";
import useAnimation from "library/useAnimation";
import useMedia from "library/useMedia";
import { getResponsivePixels } from "library/viewportUtils";
import { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import colors from "styles/colors";
import textStyles from "styles/text";

export default function Banner({ banners }: { banners: Queries.BannerQuery }) {
  const initialLoad = useRef(true);
  const wrapper = useRef<HTMLDivElement>(null);
  const outerBanner = useRef<HTMLDivElement>(null);
  const hasBeenClosed = useRef(false);
  const copyRef = useRef<HTMLParagraphElement>(null);
  const tablet = useMedia(false, false, true, false);

  const bannerToShow = banners?.allContentfulAnnouncementBannerMessage.nodes
    .filter(
      (b) =>
        (b.showMessageAt === null || new Date(b.showMessageAt) < new Date()) &&
        b.hideMessageAt &&
        new Date(b.hideMessageAt) > new Date()
    )
    .sort((a, b) => (b.messagePriority || 0) - (a.messagePriority || 0))[0];

  const openTimeline = useAnimation(
    () => {
      if (!outerBanner.current || hasBeenClosed.current) return;

      gsap.set(outerBanner.current, {
        height: getResponsivePixels(0),
      });

      const tl = gsap.timeline({
        paused: true,
        onComplete: () => {
          ScrollTrigger.refresh();
        },
      });

      {
        !tablet &&
          tl.to(
            "main",

            {
              y: getResponsivePixels(getMedia(44, 44, 60, 76)),
              duration: 1,
              ease: "power3.inOut",
            },
            0
          );
      }

      tl.fromTo(
        outerBanner.current,
        {
          height: 0,
        },
        {
          height: getResponsivePixels(getMedia(44, 44, 60, 76)),
          duration: 1,
          ease: "power3.inOut",
        },
        0
      ).to(
        copyRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: "none",
        },
        0.5
      );

      return tl;
    },
    [tablet],
    {
      recreateOnResize: false,
    }
  );

  useEffect(() => {
    const handleLoaderEnd = () => {
      if (initialLoad.current && !hasBeenClosed.current) {
        initialLoad.current = false;
        openTimeline?.play();
      }
    };

    loader.addEventListener("anyEnd", handleLoaderEnd);
    return () => loader.removeEventListener("anyEnd", handleLoaderEnd);
  }, [openTimeline]);

  const handleNavigation = (url: string) => {
    const isInternal = /^\/(?!\/)/.test(url);

    if (isInternal) {
      loadPage(url, libraryConfig.defaultTransition).catch((error: string) => {
        throw new Error(error);
      });
    } else {
      window.open(url, "_blank");
    }
  };

  const onBannerClick = (e: React.MouseEvent) => {
    if (!outerBanner.current || hasBeenClosed.current) return;

    const linkUrl = bannerToShow?.linkUrl ?? "/";

    const closingTimeline = gsap.timeline({
      onComplete: () => {
        ScrollTrigger.refresh();
        hasBeenClosed.current = true;
        handleNavigation(linkUrl);
      },
    });

    {
      !tablet &&
        closingTimeline.to(
          "main",
          {
            y: 0,
            duration: 1,
            ease: "power3.inOut",
          },
          0
        );
    }

    closingTimeline
      .to(
        [outerBanner.current, wrapper.current],
        {
          height: 0,
          duration: 1,
          ease: "power3.inOut",
        },
        0
      )
      .to(
        copyRef.current,
        {
          opacity: 0,
          duration: 0.5,
          ease: "none",
        },
        0
      )
      .to(
        [outerBanner.current, wrapper.current],
        {
          autoAlpha: 0,
          duration: 0.1,
          ease: "power3.inOut",
          onComplete: () => {
            if (outerBanner.current) {
              outerBanner.current.style.display = "none";
            }
          },
        },
        1
      );
  };

  const onClose = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!outerBanner.current || hasBeenClosed.current) return;

    const closingTimeline = gsap.timeline({
      onComplete: () => {
        ScrollTrigger.refresh();
        hasBeenClosed.current = true;
      },
    });

    {
      !tablet &&
        closingTimeline.to(
          "main",
          {
            y: 0,
            duration: 1,
            ease: "power3.inOut",
          },
          0
        );
    }

    closingTimeline
      .to(
        [outerBanner.current, wrapper.current],
        {
          height: 0,
          duration: 1,
          ease: "power3.inOut",
        },
        0
      )
      .to(
        [outerBanner.current, wrapper.current],
        {
          autoAlpha: 0,
          duration: 0.1,
          ease: "power3.inOut",
          onComplete: () => {
            if (outerBanner.current) {
              outerBanner.current.style.display = "none";
            }
          },
        },
        1
      );
  };

  if (!bannerToShow?.bannerTitle || hasBeenClosed.current) return null;

  return (
    <UniversalLink type="button" onClick={onBannerClick}>
      <Outer ref={outerBanner}>
        <OuterLayer />
        <MiddleLayer />
        <Wrapper ref={wrapper}>
          <Content ref={copyRef}>
            <Copy>{bannerToShow.bannerTitle}</Copy>
            <CloseIcon onClick={onClose}>
              <CloseSVG />
            </CloseIcon>
          </Content>
        </Wrapper>
      </Outer>
    </UniversalLink>
  );
}

const OuterLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.orangeSaturated01}20;
  transition: transform 0.4s ease-out;
  transform-origin: top;
  z-index: 1;
`;

const MiddleLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.orangeSaturated01}70;
  transition: transform 0.4s ease-out;
  transition-delay: 0.05s;
  transform-origin: top;
  z-index: 2;
`;

const CloseIcon = styled.div`
  transition: transform 0.4s ease-out;
  cursor: pointer;
  padding: 8px;

  ${fresponsive(css`
    svg {
      display: block;
      width: 16px;
      height: 16px;

      path {
        fill: ${colors.orangeBlack};
      }
    }
  `)}
`;

const Outer = styled.div`
  position: sticky;
  background: ${colors.orangeSaturated01};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500;

  ${fresponsive(css`
    gap: 16px;
    height: 44px;
    top: 0;
    width: 100vw;
  `)}

  ${ftablet(css`
    height: 60px;
  `)}

  ${fmobile(css`
    height: 56px;
  `)}

  &:hover {
    ${OuterLayer} {
      transform: scaleY(1.5);
    }

    ${MiddleLayer} {
      transform: scaleY(1.25);
    }

    ${CloseIcon} {
      transform: translateX(10px);
    }
  }
`;

const Content = styled.div`
  color: ${colors.orangeBlack};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 3;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: clip;
  opacity: 0;

  ${fresponsive(css`
    gap: 16px;
  `)}
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  box-sizing: border-box;
  overflow: clip;
  z-index: 4;

  ${fresponsive(css`
    gap: 16px;
    height: 44px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 0 110px;
    width: 1440px;
  `)}

  ${ftablet(css`
    width: 100%;
    height: 60px;
    padding: 0 70px;
  `)}

	${fmobile(css`
    width: 100%;
    height: 56px;
    padding: 0 29px;
  `)}
`;

const Copy = styled.p`
  ${textStyles.bodyS};
  word-wrap: balance;

  ${ftablet(css`
    ${textStyles.bodyS};
  `)}

  ${fmobile(css`
    ${textStyles.bodyXS};
  `)}

  * {
    display: inline;
  }
`;
